import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/shell/Toaster";
import { AuthProvider } from "@/lib/auth/context";
import { captureSignupSource, initAnalytics } from "@/lib/analytics";
import { installAutoUpdate } from "@/lib/autoUpdate";
import App from "./App";
import "./index.css";

// Sentry error monitoring (A6 FAZA A).
// DSN jest publiczny (w bundle) — z designu Sentry. Spike Protection +
// Inbound Filters w Sentry UI blokują spam.
// `sendDefaultPii: false` świadomie: nie wysyłamy IP/cookies (RODO
// minimization principle + dzieci w bazie). User uid ustawiamy ręcznie
// przez `Sentry.setUser({id})` w AuthProvider po login.
// `tracesSampleRate: 0.1` (10% requestów) — performance signal bez
// spalania Free tier 5k events/m. Session Replay świadomie WYŁĄCZONE
// (RODO Art. 8 — dzieci, brak zgody na replay).
Sentry.init({
  dsn: "https://565d068e5efa2eaddd51521ba3709269@o4511376384393216.ingest.de.sentry.io/4511376407461968",
  environment: import.meta.env.MODE === "production" ? "production" : "development",
  release: __APP_VERSION__,
  sendDefaultPii: false,
  tracesSampleRate: 0.1,
  // Wyciszamy znane noise:
  ignoreErrors: [
    // Service Worker noise między dev a prod
    "ChunkLoadError",
    "Loading chunk",
    "Loading CSS chunk",
    // Firebase wewnętrzne timeouts
    "FIRESTORE.*INTERNAL ASSERTION FAILED",
    // ResizeObserver loop limit reached - benign browser quirk
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed",
    // Firebase App Check noise od crawlerów/botów (FB link preview itp.):
    // reCAPTCHA v3 odmawia tokena botowi → 403 → SDK retry render invisible
    // widget na tym samym <div> → unhandled error z setTimeout.
    "reCAPTCHA has already been rendered in this element",
    "appCheck/initial-throttle",
  ],
});

// Cichy auto-update: po starcie + przy wybudzeniu z backgroundu
// porównuje __BUILD_HASH__ z /version.json. Reload przy różnicy.
installAutoUpdate();

// PostHog (faza 0) — no-op bez VITE_POSTHOG_KEY. captureSignupSource
// łapie ?ref=/?utm_source= zanim router cokolwiek przepisze.
initAnalytics();
captureSignupSource();

// One-time cleanup po migracji Next.js → Vite SPA.
// Stary Service Worker był pod `/api/messaging-sw` + cachował HTML/JS.
// Tu unregister wszystkich SW poza naszym `firebase-messaging-sw.js` + clear
// wszystkich Cache Storage. Wykonuje się raz przy każdym wczytaniu — szybkie.
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then((regs) => {
    regs.forEach((r) => {
      const url = r.active?.scriptURL ?? "";
      if (!url.endsWith("/firebase-messaging-sw.js")) {
        void r.unregister();
      }
    });
  });
  if ("caches" in window) {
    void caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-dvh flex flex-col items-center justify-center p-6 text-center">
          <h1 className="text-xl font-semibold mb-2">Coś poszło nie tak</h1>
          <p className="text-sm text-muted mb-4">
            {error instanceof Error ? error.message : "Nieznany błąd"}
          </p>
          <button
            type="button"
            onClick={resetError}
            className="h-11 px-6 rounded-[12px] bg-accent text-white font-semibold"
          >
            Spróbuj ponownie
          </button>
        </div>
      )}
    >
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
            <Toaster />
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
